import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen, Flame, Inbox, Lock, Quote, Check, Trash2, Clock, AlertTriangle, Mail, CheckCircle2, XCircle, RefreshCw, CreditCard, Database } from "lucide-react";
import { toast } from "sonner";

const EspaceMembre = () => {
  const { user, loading } = useAuth();
  const [adminMessages, setAdminMessages] = useState([]);
  const [myTestimonies, setMyTestimonies] = useState([]);
  const [adminTestimonies, setAdminTestimonies] = useState([]);
  const [diag, setDiag] = useState(null);
  const [stripeDiag, setStripeDiag] = useState(null);
  const [mongoDiag, setMongoDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const loadMyTestimonies = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonies/mine");
      setMyTestimonies(data);
    } catch (_e) { /* silent */ }
  }, []);

  const loadDiagnostic = useCallback(async () => {
    setDiagLoading(true);
    try {
      const [email, stripe, mongo] = await Promise.all([
        api.get("/admin/email-diagnostic"),
        api.get("/admin/stripe-diagnostic"),
        api.get("/admin/mongo-diagnostic"),
      ]);
      setDiag(email.data);
      setStripeDiag(stripe.data);
      setMongoDiag(mongo.data);
    } catch (_e) {
      setDiag({ error: "Impossible de récupérer le diagnostic" });
    } finally {
      setDiagLoading(false);
    }
  }, []);

  const loadAdminData = useCallback(async () => {
    try {
      const [msgs, tests] = await Promise.all([
        api.get("/admin/prayer-requests"),
        api.get("/admin/testimonies"),
      ]);
      setAdminMessages(msgs.data);
      setAdminTestimonies(tests.data);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.is_donor) loadMyTestimonies();
    if (user.is_admin) {
      loadAdminData();
      loadDiagnostic();
    }
  }, [user, loadMyTestimonies, loadAdminData, loadDiagnostic]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-20 text-[var(--ivory-muted)] italic">Recueillement…</div>;
  if (!user) return <Navigate to="/connexion" state={{ from: "/espace-membre" }} />;

  const approveTestimony = async (tid) => {
    try {
      await api.put(`/admin/testimonies/${tid}/approve`);
      toast.success("Témoignage approuvé.");
      loadAdminData();
    } catch (_e) { toast.error("Action impossible."); }
  };

  const deleteTestimony = async (tid) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await api.delete(`/admin/testimonies/${tid}`);
      toast.success("Témoignage supprimé.");
      loadAdminData();
    } catch (_e) { toast.error("Action impossible."); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="member-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <p className="font-engraved text-[var(--gold)] text-[11px] mb-3">Espace du gardien</p>
          <h1 className="font-serif-display text-5xl text-[var(--ivory)]">
            Bonjour, <em className="text-[var(--gold)]">{user.name}</em>
          </h1>
          <p className="font-serif-body text-[var(--ivory-muted)] mt-3">
            {user.is_donor ? (
              <span className="flex items-center gap-2"><Flame size={14} className="text-[var(--gold)]" /> Gardien du sanctuaire — accès complet aux prières sacrées.</span>
            ) : (
              <span>Compte créé avec succès — bienvenue au seuil du sanctuaire.</span>
            )}
          </p>
        </div>
        {!user.is_donor && (
          <Link to="/abonnement" className="btn-sacred btn-sacred-filled sharp" data-testid="become-donor-cta">
            Devenir gardien — 29€
          </Link>
        )}
      </div>

      {/* NON-DONOR VIEW */}
      {!user.is_donor && (
        <div className="sacred-card sharp p-10 md:p-14" data-testid="welcome-non-donor">
          <div className="max-w-2xl mx-auto text-center">
            <Lock className="mx-auto text-[var(--gold)] mb-6" strokeWidth={1.1} size={40} />
            <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--ivory)] mb-6 leading-tight">
              Votre compte est <em className="text-[var(--gold)]">créé</em>
            </h2>
            <p className="font-serif-body text-[var(--ivory-muted)] text-lg leading-relaxed mb-8">
              Vous êtes désormais inscrit(e) sur le sanctuaire. Vous pouvez dès à présent
              consulter la bibliothèque des prières libres — plusieurs dizaines de textes
              vous sont ouverts sans engagement.
            </p>
            <div className="divider-ornament my-10"><Flame size={14} strokeWidth={1.2} /></div>
            <p className="font-serif-body text-[var(--ivory-muted)] text-lg leading-relaxed mb-8">
              Pour accéder aux <strong className="text-[var(--ivory)]">prières scellées</strong> — les oraisons les plus rares,
              les rituels traditionnels et les textes de délivrance — une offrande unique de{" "}
              <strong className="text-[var(--gold)]">29 €</strong> vous ouvre l'ensemble du sanctuaire, <em>à vie</em>, sans renouvellement.
            </p>
            <ul className="text-left max-w-md mx-auto space-y-3 mb-10 font-serif-body text-[var(--ivory)]">
              <li className="flex items-start gap-3"><Flame className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.4} size={16} /><span>Accès à toutes les prières scellées, sans exception</span></li>
              <li className="flex items-start gap-3"><Flame className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.4} size={16} /><span>Accès à toutes les futures prières ajoutées au sanctuaire</span></li>
              <li className="flex items-start gap-3"><Flame className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.4} size={16} /><span>Possibilité de partager votre témoignage</span></li>
              <li className="flex items-start gap-3"><Flame className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.4} size={16} /><span>Un seul paiement — jamais de renouvellement</span></li>
            </ul>
            <Link to="/abonnement" className="btn-sacred btn-sacred-filled sharp inline-block" data-testid="welcome-subscribe-cta">
              Devenir gardien — 29 €
            </Link>
            <p className="font-engraved text-[9px] text-[var(--ivory-muted)] tracking-widest mt-6">
              OU <Link to="/bibliotheque" className="text-[var(--gold)] hover:opacity-70 transition" data-testid="browse-free-link">EXPLORER LES PRIÈRES LIBRES →</Link>
            </p>
          </div>
        </div>
      )}

      {/* DONOR VIEW */}
      {user.is_donor && (
        <>
          {/* Access shortcut */}
          <div className="sacred-card sharp p-8 md:p-10 mb-10 flex flex-col md:flex-row items-center gap-6 justify-between" data-testid="donor-shortcut">
            <div className="flex items-center gap-4">
              <BookOpen className="text-[var(--gold)] shrink-0" strokeWidth={1.1} size={36} />
              <div>
                <h2 className="font-serif-display text-2xl text-[var(--ivory)]">Toutes les prières vous sont ouvertes</h2>
                <p className="font-serif-body text-[var(--ivory-muted)] text-sm mt-1">
                  Bibliothèque complète — prières libres et scellées.
                </p>
              </div>
            </div>
            <Link to="/bibliotheque" className="btn-sacred btn-sacred-filled sharp" data-testid="go-library">
              Ouvrir la bibliothèque
            </Link>
          </div>

          {/* Testimony section: shortcut to /temoignages + status list */}
          <div className="grid lg:grid-cols-5 gap-px bg-[rgba(212,175,55,0.12)] mb-10">
            {/* CTA to share testimony */}
            <div className="sacred-card sharp p-10 lg:col-span-3 flex flex-col justify-center" data-testid="testimony-cta-card">
              <div className="flex items-center gap-3 mb-4">
                <Quote className="text-[var(--gold)]" strokeWidth={1.1} size={28} />
                <h2 className="font-serif-display text-3xl text-[var(--ivory)]">Partagez votre expérience</h2>
              </div>
              <p className="font-serif-body text-[var(--ivory-muted)] mb-6 leading-relaxed">
                Votre parole encourage d'autres âmes en chemin. Ajoutez votre témoignage
                sur la page dédiée du sanctuaire — après relecture, il sera publié.
              </p>
              <Link to="/temoignages" className="btn-sacred btn-sacred-filled sharp inline-flex items-center gap-2 w-fit" data-testid="go-testimonies">
                Aller à la page témoignages
              </Link>
            </div>

            {/* Mes témoignages */}
            <div className="sacred-card sharp p-10 lg:col-span-2" data-testid="my-testimonies">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="text-[var(--gold)]" strokeWidth={1.1} size={24} />
                <h2 className="font-serif-display text-2xl text-[var(--ivory)]">Mes témoignages</h2>
              </div>
              {myTestimonies.length === 0 ? (
                <p className="font-serif-body italic text-[var(--ivory-muted)]">
                  Vous n'avez pas encore partagé de témoignage.
                </p>
              ) : (
                <ul className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                  {myTestimonies.map((t) => (
                    <li key={t.id} className="border-l-2 border-[var(--gold)]/40 pl-4" data-testid={`my-testimony-${t.id}`}>
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <p className="font-engraved text-[10px] text-[var(--gold)] tracking-widest">
                          {new Date(t.created_at).toLocaleDateString("fr-FR")} · {t.category}
                        </p>
                        {t.is_approved ? (
                          <span className="font-engraved text-[9px] text-[var(--gold)] flex items-center gap-1">
                            <Check size={11} strokeWidth={2} /> PUBLIÉ
                          </span>
                        ) : (
                          <span className="font-engraved text-[9px] text-[var(--ivory-muted)] flex items-center gap-1">
                            <Clock size={11} strokeWidth={2} /> EN RELECTURE
                          </span>
                        )}
                      </div>
                      <p className="font-serif-body italic text-[var(--ivory)] text-sm leading-relaxed">
                        « {t.text.length > 180 ? t.text.slice(0, 180) + "…" : t.text} »
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {/* ADMIN — System health diagnostic */}
      {user.is_admin && (
        <div className="sacred-card sharp p-10 mt-10 border-t-2 border-[var(--gold)]/30" data-testid="admin-diagnostic">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="text-[var(--gold)]" strokeWidth={1.1} size={28} />
            <h2 className="font-serif-display text-3xl text-[var(--ivory)]">
              Santé <em className="text-[var(--gold)]">du système</em>
            </h2>
            <button
              onClick={loadDiagnostic}
              disabled={diagLoading}
              className="ml-auto font-engraved text-[10px] text-[var(--gold)] tracking-widest hover:opacity-70 transition flex items-center gap-1"
              data-testid="refresh-diagnostic"
            >
              <RefreshCw size={12} className={diagLoading ? "animate-spin" : ""} /> {diagLoading ? "VÉRIFICATION…" : "RAFRAÎCHIR"}
            </button>
          </div>

          {!diag ? (
            <p className="font-serif-body italic text-[var(--ivory-muted)]">Chargement du diagnostic…</p>
          ) : diag.error && !diag.tcp_connection ? (
            <div className="border-l-4 border-l-[var(--bordeaux)] pl-4 py-2">
              <p className="font-engraved text-[10px] text-[var(--bordeaux)] mb-1">ERREUR</p>
              <p className="font-serif-body text-[var(--ivory)]">{diag.error}</p>
            </div>
          ) : (
            <>
              {/* Warnings for obsolete env vars */}
              {diag.env_warnings && diag.env_warnings.length > 0 && (
                <div className="mb-6 border-l-4 border-l-[var(--bordeaux)] pl-4 py-2 bg-[rgba(139,26,26,0.1)]" data-testid="env-warnings">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="text-[var(--bordeaux)]" size={16} strokeWidth={1.5} />
                    <p className="font-engraved text-[10px] text-[var(--bordeaux)] tracking-widest">
                      {diag.env_warnings.length} ALERTE{diag.env_warnings.length > 1 ? "S" : ""} DE CONFIGURATION
                    </p>
                  </div>
                  <ul className="space-y-2">
                    {diag.env_warnings.map((w) => (
                      <li key={w.slice(0, 30)} className="font-serif-body text-sm text-[var(--ivory)] leading-relaxed">
                        ⚠️ {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Status grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">SERVEUR SMTP</p>
                  <p className="font-serif-body text-[var(--ivory)] text-sm">
                    {diag.smtp_host}:{diag.smtp_port}
                  </p>
                </div>
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">EXPÉDITEUR UTILISÉ</p>
                  <p className="font-serif-body text-[var(--ivory)] text-sm">{diag.sender_email_used}</p>
                </div>
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">EMAIL DE NOTIFICATION</p>
                  <p className="font-serif-body text-[var(--ivory)] text-sm">{diag.admin_notify_email}</p>
                </div>
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">CONNEXION TCP</p>
                  <p className="font-serif-body text-sm flex items-center gap-2">
                    {diag.tcp_connection === "OK" ? (
                      <><CheckCircle2 className="text-[var(--gold)]" size={14} strokeWidth={2} /> <span className="text-[var(--ivory)]">Opérationnelle</span></>
                    ) : (
                      <><XCircle className="text-[var(--bordeaux)]" size={14} strokeWidth={2} /> <span className="text-[var(--bordeaux)]">{diag.tcp_connection}</span></>
                    )}
                  </p>
                </div>
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">AUTHENTIFICATION SMTP</p>
                  <p className="font-serif-body text-sm flex items-center gap-2">
                    {diag.smtp_auth === "OK" ? (
                      <><CheckCircle2 className="text-[var(--gold)]" size={14} strokeWidth={2} /> <span className="text-[var(--ivory)]">Réussie</span></>
                    ) : (
                      <><XCircle className="text-[var(--bordeaux)]" size={14} strokeWidth={2} /> <span className="text-[var(--bordeaux)]">Échec</span></>
                    )}
                  </p>
                </div>
                <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                  <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">ENVOI TEST</p>
                  <p className="font-serif-body text-sm flex items-center gap-2">
                    {diag.test_send === "sent" ? (
                      <><CheckCircle2 className="text-[var(--gold)]" size={14} strokeWidth={2} /> <span className="text-[var(--ivory)]">Message envoyé</span></>
                    ) : (
                      <><XCircle className="text-[var(--bordeaux)]" size={14} strokeWidth={2} /> <span className="text-[var(--bordeaux)]">Non envoyé</span></>
                    )}
                  </p>
                </div>
              </div>

              {diag.error && (
                <div className="mt-4 border-l-4 border-l-[var(--bordeaux)] pl-4 py-2">
                  <p className="font-engraved text-[10px] text-[var(--bordeaux)] mb-1">DÉTAIL ERREUR</p>
                  <p className="font-serif-body text-xs text-[var(--ivory-muted)] break-all">{diag.error}</p>
                </div>
              )}

              <p className="font-engraved text-[9px] text-[var(--ivory-muted)] tracking-widest mt-6">
                📩 UN EMAIL DE TEST EST ENVOYÉ À {diag.admin_notify_email?.toUpperCase()} À CHAQUE RAFRAÎCHISSEMENT
              </p>

              {/* --- STRIPE STATUS --- */}
              <div className="mt-10 pt-8 border-t border-[rgba(212,175,55,0.15)]" data-testid="stripe-diag">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="text-[var(--gold)]" strokeWidth={1.1} size={22} />
                  <h3 className="font-serif-display text-2xl text-[var(--ivory)]">Paiements <em className="text-[var(--gold)]">— Stripe</em></h3>
                </div>
                {!stripeDiag ? (
                  <p className="font-serif-body italic text-[var(--ivory-muted)]">Chargement…</p>
                ) : !stripeDiag.reachable ? (
                  <div className="border-l-4 border-l-[var(--bordeaux)] pl-4 py-2">
                    <p className="font-engraved text-[10px] text-[var(--bordeaux)] mb-1">STRIPE INACCESSIBLE</p>
                    <p className="font-serif-body text-xs text-[var(--ivory-muted)] break-all">{stripeDiag.error}</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                      <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">MODE</p>
                      <p className="font-serif-body text-sm flex items-center gap-2">
                        {stripeDiag.mode === "LIVE" ? (
                          <><CheckCircle2 className="text-[var(--gold)]" size={14} strokeWidth={2} /> <span className="text-[var(--ivory)]">LIVE (paiements réels)</span></>
                        ) : (
                          <><AlertTriangle className="text-[var(--bordeaux)]" size={14} strokeWidth={2} /> <span className="text-[var(--bordeaux)]">TEST (sandbox)</span></>
                        )}
                      </p>
                    </div>
                    <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                      <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">COMPTE</p>
                      <p className="font-serif-body text-sm text-[var(--ivory)] break-all">{stripeDiag.account_email}</p>
                    </div>
                    <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                      <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">ENCAISSEMENT ACTIVÉ</p>
                      <p className="font-serif-body text-sm flex items-center gap-2">
                        {stripeDiag.charges_enabled ? (
                          <><CheckCircle2 className="text-[var(--gold)]" size={14} strokeWidth={2} /> <span className="text-[var(--ivory)]">Oui</span></>
                        ) : (
                          <><XCircle className="text-[var(--bordeaux)]" size={14} strokeWidth={2} /> <span className="text-[var(--bordeaux)]">Non</span></>
                        )}
                      </p>
                    </div>
                    <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                      <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">PAIEMENTS ENCAISSÉS</p>
                      <p className="font-serif-body text-lg text-[var(--gold)]">{stripeDiag.completed_payments}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* --- MONGO STATUS --- */}
              <div className="mt-10 pt-8 border-t border-[rgba(212,175,55,0.15)]" data-testid="mongo-diag">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="text-[var(--gold)]" strokeWidth={1.1} size={22} />
                  <h3 className="font-serif-display text-2xl text-[var(--ivory)]">Base de données <em className="text-[var(--gold)]">— MongoDB</em></h3>
                </div>
                {!mongoDiag ? (
                  <p className="font-serif-body italic text-[var(--ivory-muted)]">Chargement…</p>
                ) : !mongoDiag.reachable ? (
                  <div className="border-l-4 border-l-[var(--bordeaux)] pl-4 py-2">
                    <p className="font-engraved text-[10px] text-[var(--bordeaux)] mb-1">MONGO INACCESSIBLE</p>
                    <p className="font-serif-body text-xs text-[var(--ivory-muted)] break-all">{mongoDiag.error}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-center gap-3">
                      <CheckCircle2 className="text-[var(--gold)]" size={16} strokeWidth={2} />
                      <span className="font-serif-body text-[var(--ivory)] text-sm">
                        Connexion opérationnelle — <span className="text-[var(--gold)]">{mongoDiag.latency_ms} ms</span> de latence
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                        <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">PRIÈRES</p>
                        <p className="font-serif-display text-2xl text-[var(--ivory)]">{mongoDiag.prayers}</p>
                      </div>
                      <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                        <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">COMPTES</p>
                        <p className="font-serif-display text-2xl text-[var(--ivory)]">{mongoDiag.users}</p>
                      </div>
                      <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                        <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">ABONNÉS</p>
                        <p className="font-serif-display text-2xl text-[var(--gold)]">{mongoDiag.donors}</p>
                      </div>
                      <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                        <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">TÉMOIGNAGES</p>
                        <p className="font-serif-display text-lg text-[var(--ivory)]">
                          {mongoDiag.testimonies_total}
                          {mongoDiag.testimonies_pending > 0 && (
                            <span className="text-[var(--bordeaux)] text-sm ml-2">({mongoDiag.testimonies_pending} en attente)</span>
                          )}
                        </p>
                      </div>
                      <div className="border-l-2 border-[var(--gold)]/40 pl-4">
                        <p className="font-engraved text-[10px] text-[var(--gold)] mb-2 tracking-widest">MESSAGES REÇUS</p>
                        <p className="font-serif-display text-2xl text-[var(--ivory)]">{mongoDiag.messages}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ADMIN — Messages */}
      {user.is_admin && (
        <div className="sacred-card sharp p-10 mt-10 border-t-2 border-[var(--gold)]/30" data-testid="admin-messages">
          <div className="flex items-center gap-3 mb-6">
            <Inbox className="text-[var(--gold)]" strokeWidth={1.1} size={28} />
            <h2 className="font-serif-display text-3xl text-[var(--ivory)]">
              Messagerie <em className="text-[var(--gold)]">— Admin</em>
            </h2>
            <span className="ml-auto font-engraved text-[10px] text-[var(--ivory-muted)] tracking-widest">
              {adminMessages.length} MESSAGE{adminMessages.length > 1 ? "S" : ""}
            </span>
          </div>
          {adminMessages.length === 0 ? (
            <p className="font-serif-body italic text-[var(--ivory-muted)]">Aucun message reçu pour l'instant.</p>
          ) : (
            <ul className="space-y-6 max-h-[500px] overflow-y-auto pr-3">
              {adminMessages.map((m) => (
                <li key={m.id} className="border-l-2 border-[var(--gold)]/40 pl-5" data-testid={`admin-msg-${m.id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                    <p className="font-serif-display text-lg text-[var(--ivory)]">
                      {m.name} <span className="text-[var(--ivory-muted)] text-sm font-serif-body">— {m.email}</span>
                    </p>
                    <p className="font-engraved text-[10px] text-[var(--gold)] tracking-widest">
                      {new Date(m.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <p className="font-serif-body text-[var(--ivory)] whitespace-pre-line leading-relaxed">{m.intention}</p>
                  <a href={`mailto:${m.email}?subject=Réponse à votre message&body=Bonjour ${m.name},%0D%0A%0D%0A`}
                    className="inline-block mt-3 font-engraved text-[10px] text-[var(--gold)] tracking-widest hover:opacity-70 transition"
                    data-testid={`admin-reply-${m.id}`}>RÉPONDRE PAR EMAIL →</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ADMIN — Testimonies moderation */}
      {user.is_admin && (
        <div className="sacred-card sharp p-10 mt-10 border-t-2 border-[var(--gold)]/30" data-testid="admin-testimonies">
          <div className="flex items-center gap-3 mb-6">
            <Quote className="text-[var(--gold)]" strokeWidth={1.1} size={28} />
            <h2 className="font-serif-display text-3xl text-[var(--ivory)]">
              Témoignages <em className="text-[var(--gold)]">— Modération</em>
            </h2>
            <span className="ml-auto font-engraved text-[10px] text-[var(--ivory-muted)] tracking-widest">
              {adminTestimonies.filter((t) => !t.is_approved).length} EN ATTENTE / {adminTestimonies.length} AU TOTAL
            </span>
          </div>
          {adminTestimonies.length === 0 ? (
            <p className="font-serif-body italic text-[var(--ivory-muted)]">Aucun témoignage pour l'instant.</p>
          ) : (
            <ul className="space-y-6 max-h-[600px] overflow-y-auto pr-3">
              {adminTestimonies.map((t) => (
                <li key={t.id} className={`border-l-2 pl-5 ${t.is_approved ? "border-[var(--gold)]/40" : "border-[var(--bordeaux)]"}`} data-testid={`admin-testimony-${t.id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                    <p className="font-serif-display text-lg text-[var(--ivory)]">
                      {t.name} <span className="text-[var(--ivory-muted)] text-sm font-serif-body">— {t.city} — {t.category}</span>
                    </p>
                    <div className="flex items-center gap-3">
                      {t.is_approved ? (
                        <span className="font-engraved text-[10px] text-[var(--gold)]">PUBLIÉ</span>
                      ) : (
                        <span className="font-engraved text-[10px] text-[var(--bordeaux)]">EN ATTENTE</span>
                      )}
                      <p className="font-engraved text-[10px] text-[var(--ivory-muted)]">
                        {new Date(t.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                  <p className="font-serif-body italic text-[var(--ivory)] leading-relaxed mb-3">« {t.text} »</p>
                  <div className="flex gap-3">
                    {!t.is_approved && (
                      <button onClick={() => approveTestimony(t.id)}
                        className="font-engraved text-[10px] text-[var(--gold)] tracking-widest hover:opacity-70 transition flex items-center gap-1"
                        data-testid={`approve-${t.id}`}>
                        <Check size={12} /> APPROUVER
                      </button>
                    )}
                    <button onClick={() => deleteTestimony(t.id)}
                      className="font-engraved text-[10px] text-[var(--bordeaux)] tracking-widest hover:opacity-70 transition flex items-center gap-1"
                      data-testid={`delete-${t.id}`}>
                      <Trash2 size={12} /> SUPPRIMER
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EspaceMembre;
